import React, { useEffect, useState, useCallback } from 'react';
import {
  Table,
  Tag,
  Button,
  Form,
  Checkbox,
  Select,
  DatePicker,
  Space,
  Progress,
  Badge,
  Typography,
} from 'antd';
import type { TableColumnsType } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import {
  getQueue,
  type KycQueueItem,
  type KycStatus,
} from '../../services/adminKycApiService';
import KycReviewDrawer from './KycReviewDrawer';

const { RangePicker } = DatePicker;
const { Title } = Typography;

type FilterForm = {
  status: KycStatus[];
  aiVerdict: string;
  dateRange: [Dayjs, Dayjs] | null;
};

const STATUS_COLORS: Record<KycStatus, string> = {
  PENDING: 'gold',
  AI_REVIEWING: 'processing',
  MANUAL_REVIEW: 'orange',
  APPROVED: 'success',
  REJECTED: 'error',
  EXPIRED: 'default',
};

const DOC_LABELS: Record<string, string> = {
  SA_SMART_ID: 'Smart ID card',
  SA_GREEN_ID: 'Green ID book',
  PASSPORT: 'Passport',
};

/** Paginated KYC review queue with filters, sorting and a review drawer. */
export function KycQueuePage() {
  const [form] = Form.useForm<FilterForm>();
  const [items, setItems] = useState<KycQueueItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [sort, setSort] = useState('submittedAt,desc');
  const [filters, setFilters] = useState<Partial<FilterForm>>({});
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const load = useCallback(
    async (p = 0, s = sort, f = filters) => {
      setLoading(true);
      try {
        const statuses: KycStatus[] =
          f.status && f.status.length > 0
            ? f.status
            : ['PENDING', 'AI_REVIEWING', 'MANUAL_REVIEW'];
        const res = await getQueue({ status: statuses, page: p, size: 20, sort: s });
        setItems(res.content);
        setTotal(res.totalElements);
      } finally {
        setLoading(false);
      }
    },
    [sort, filters]
  );

  useEffect(() => {
    load(0, sort, filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => {
    const vals = form.getFieldsValue();
    setFilters(vals);
    setPage(0);
    load(0, sort, vals);
  };

  const handleReset = () => {
    form.resetFields();
    setFilters({});
    setPage(0);
    load(0, sort, {});
  };

  const openReview = (id: string) => {
    setSelectedId(id);
    setDrawerOpen(true);
  };

  const columns: TableColumnsType<KycQueueItem> = [
    {
      title: 'Provider',
      dataIndex: 'providerName',
      render: (name: string) => (
        <span style={{ fontWeight: 500 }}>{name}</span>
      ),
    },
    {
      title: 'Document',
      dataIndex: 'documentType',
      render: (t: string) => DOC_LABELS[t] ?? t,
    },
    {
      title: 'Submitted',
      dataIndex: 'submittedAt',
      sorter: true,
      render: (v: string) => dayjs(v).format('DD/MM/YYYY HH:mm'),
    },
    {
      title: 'AI Verdict',
      dataIndex: 'aiVerdict',
      render: (v: string | null) => {
        if (!v) return <Tag color="default">—</Tag>;
        const colorMap: Record<string, string> = {
          PASS: 'green',
          FAIL: 'red',
          MANUAL_REVIEW: 'orange',
        };
        return <Tag color={colorMap[v] ?? 'default'}>{v}</Tag>;
      },
    },
    {
      title: 'Confidence',
      dataIndex: 'aiConfidence',
      sorter: true,
      render: (v: number | null) =>
        v !== null ? (
          <Progress
            percent={v}
            size="small"
            strokeColor={v >= 80 ? '#52c41a' : v >= 50 ? '#faad14' : '#ff4d4f'}
            style={{ minWidth: 80 }}
          />
        ) : (
          <span style={{ color: '#aaa' }}>—</span>
        ),
    },
    {
      title: 'Flags',
      dataIndex: 'flagCount',
      render: (n: number) =>
        n > 0 ? <Badge count={n} color="red" /> : <span style={{ color: '#aaa' }}>—</span>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (s: KycStatus) => (
        <Tag color={STATUS_COLORS[s]}>{s.replace('_', ' ')}</Tag>
      ),
    },
    {
      title: 'Actions',
      render: (_: unknown, row: KycQueueItem) => (
        <Button size="small" type="primary" onClick={() => openReview(row.submissionId)}>
          Review
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Title level={3} style={{ marginBottom: 16 }}>
        KYC Review Queue
      </Title>

      {/* Filters */}
      <Form
        form={form}
        layout="inline"
        style={{ marginBottom: 16, flexWrap: 'wrap', gap: 8 }}
      >
        <Form.Item name="status" label="Status">
          <Checkbox.Group
            options={[
              { label: 'Pending', value: 'PENDING' },
              { label: 'AI Reviewing', value: 'AI_REVIEWING' },
              { label: 'Manual Review', value: 'MANUAL_REVIEW' },
              { label: 'Approved', value: 'APPROVED' },
              { label: 'Rejected', value: 'REJECTED' },
            ]}
          />
        </Form.Item>
        <Form.Item name="aiVerdict" label="AI Verdict">
          <Select
            placeholder="Any"
            allowClear
            style={{ width: 140 }}
            options={[
              { label: 'PASS', value: 'PASS' },
              { label: 'FAIL', value: 'FAIL' },
              { label: 'MANUAL_REVIEW', value: 'MANUAL_REVIEW' },
            ]}
          />
        </Form.Item>
        <Form.Item name="dateRange" label="Submitted">
          <RangePicker format="DD/MM/YYYY" />
        </Form.Item>
        <Form.Item>
          <Space>
            <Button type="primary" onClick={handleSearch}>
              Search
            </Button>
            <Button onClick={handleReset}>Reset</Button>
          </Space>
        </Form.Item>
      </Form>

      <Table
        columns={columns}
        dataSource={items}
        rowKey="submissionId"
        loading={loading}
        pagination={{
          current: page + 1,
          total,
          pageSize: 20,
          onChange: (p) => {
            setPage(p - 1);
            load(p - 1, sort, filters);
          },
          showTotal: (t) => `${t} submissions`,
        }}
        onChange={(_p, _f, sorter) => {
          if (!Array.isArray(sorter) && sorter.columnKey) {
            const dir = sorter.order === 'ascend' ? 'asc' : 'desc';
            const newSort = `${String(sorter.columnKey)},${dir}`;
            setSort(newSort);
            load(page, newSort, filters);
          }
        }}
      />

      <KycReviewDrawer
        submissionId={selectedId}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onActionComplete={() => {
          setDrawerOpen(false);
          load(page, sort, filters);
        }}
      />
    </div>
  );
}

export default KycQueuePage;
